/**
 * Catalogue des centres d'intérêt et des couleurs de tribu.
 *
 * Le participant qui ouvre une tribu choisit **un sujet**, jamais une
 * couleur : celle-ci est attribuée au hasard à la création, parmi celles
 * encore libres dans le quartier. Deux raisons à cela — la couleur sert à se
 * reconnaître sur place, elle doit donc être unique à un instant donné ; et
 * personne ne « possède » une couleur, ce qui évite de la voir devenir un
 * signe d'appartenance ou de statut.
 */

/**
 * Une question d'attente : un énoncé, trois réponses. Deux humains qui
 * répondent pareil se découvrent un point commun à la fin de la soirée.
 *
 * La forme est celle de `zQuestion` (paquet `protocol`, `model.ts`), recopiée
 * ici plutôt qu'importée : le catalogue est une donnée, il ne doit pas
 * dépendre du schéma qui le valide.
 */
export interface QuestionSujet {
  id: string;
  text: string;
  a: string;
  b: string;
  c: string;
}

export interface Sujet {
  id: string;
  /** Formulé à la première personne, comme dans le prototype. */
  label: string;
  /** Prétexte commun donné à la tribu une fois réunie. */
  mission: string;
}

export interface FamilleSujets {
  id: string;
  label: string;
  sujets: Sujet[];
}

export const FAMILLES: readonly FamilleSujets[] = [
  {
    id: "dehors",
    label: "Dehors",
    sujets: [
      { id: "marcher", label: "J'aime marcher", mission: "Rejoignez le point de rendez-vous à pied" },
      { id: "courir", label: "J'aime courir le matin", mission: "Dites à quelle heure vous vous levez" },
      { id: "velo", label: "J'aime le vélo en ville", mission: "Comparez vos pires côtes" },
      { id: "parcs", label: "J'aime les parcs et les bancs", mission: "Trouvez le meilleur banc du quartier" },
      { id: "nuit", label: "J'aime la ville la nuit", mission: "Dites ce que la nuit change ici" },
      { id: "randonnee", label: "J'aime partir en randonnée", mission: "Proposez une sortie à faire un dimanche" },
    ],
  },
  {
    id: "sport",
    label: "Sport",
    sujets: [
      { id: "ol", label: "J'aime l'Olympique lyonnais", mission: "Trouvez votre cri de ralliement" },
      { id: "foot", label: "J'aime regarder le foot", mission: "Racontez un match dont vous vous souvenez" },
      { id: "basket", label: "J'aime le basket", mission: "Trouvez le terrain le plus proche" },
      { id: "escalade", label: "J'aime grimper", mission: "Montrez vos mains" },
      { id: "natation", label: "J'aime nager", mission: "Dites où vous nagez en été" },
      { id: "arts-martiaux", label: "J'aime les arts martiaux", mission: "Expliquez pourquoi vous avez commencé" },
    ],
  },
  {
    id: "musique",
    label: "Musique",
    sujets: [
      { id: "musique", label: "J'aime la musique", mission: "Faites écouter un titre à quelqu'un" },
      { id: "concerts", label: "J'aime les concerts", mission: "Racontez le dernier que vous avez vu" },
      { id: "rap", label: "J'aime le rap", mission: "Citez un couplet que vous connaissez par cœur" },
      { id: "electro", label: "J'aime l'électro", mission: "Trouvez le rythme commun" },
      { id: "chanson", label: "J'aime la chanson française", mission: "Chantez un vers, même faux" },
      { id: "instrument", label: "Je joue d'un instrument", mission: "Dites ce que vous jouez et depuis quand" },
    ],
  },
  {
    id: "histoires",
    label: "Histoires",
    sujets: [
      { id: "series", label: "J'aime les séries", mission: "Choisissez une scène à rejouer" },
      { id: "cinema", label: "J'aime le cinéma", mission: "Trouvez un film que vous avez tous vu" },
      { id: "lire", label: "J'aime lire", mission: "Dites ce que vous lisez en ce moment" },
      { id: "bd", label: "J'aime la bande dessinée", mission: "Décrivez une case qui vous a marqué" },
      { id: "podcasts", label: "J'aime les podcasts", mission: "Recommandez un épisode" },
      { id: "science-fiction", label: "J'aime la science-fiction", mission: "Inventez la suite d'un monde" },
    ],
  },
  {
    id: "table",
    label: "À table",
    sujets: [
      { id: "manger", label: "J'aime manger quelque part", mission: "Trouvez ce que vous partagerez" },
      { id: "cuisiner", label: "J'aime cuisiner", mission: "Échangez une recette en trois phrases" },
      { id: "cafe", label: "J'aime les cafés", mission: "Désignez le meilleur café du coin" },
      { id: "boulangerie", label: "J'aime les boulangeries", mission: "Départagez deux boulangeries" },
      { id: "marches", label: "J'aime les marchés", mission: "Dites ce que vous achetez toujours" },
      { id: "vin", label: "J'aime le vin et la bière", mission: "Trouvez ce que vous boiriez ensemble" },
    ],
  },
  {
    id: "faire",
    label: "Faire",
    sujets: [
      { id: "dessiner", label: "J'aime dessiner", mission: "Dessinez-vous les uns les autres en une minute" },
      { id: "photo", label: "J'aime la photo", mission: "Cadrez la même chose, chacun à sa façon" },
      { id: "ecrire", label: "J'aime écrire", mission: "Écrivez une phrase à quatre mains" },
      { id: "bricoler", label: "J'aime bricoler et réparer", mission: "Racontez votre pire réparation" },
      { id: "jardiner", label: "J'aime les plantes", mission: "Trouvez une plante autour de vous" },
      { id: "coudre", label: "J'aime coudre et fabriquer", mission: "Montrez quelque chose que vous avez fait" },
    ],
  },
  {
    id: "jeux",
    label: "Jeux et curiosités",
    sujets: [
      { id: "jeux-societe", label: "J'aime les jeux de société", mission: "Choisissez le jeu de la prochaine fois" },
      { id: "jeux-video", label: "J'aime les jeux vidéo", mission: "Trouvez un jeu que vous avez tous fini" },
      { id: "enigmes", label: "J'aime les énigmes", mission: "Posez-en une, tout de suite" },
      { id: "histoire-locale", label: "J'aime l'histoire de ma ville", mission: "Trouvez ce que ce lieu était avant" },
      { id: "sciences", label: "J'aime les sciences", mission: "Expliquez une chose que personne ne sait" },
      { id: "langues", label: "J'aime apprendre des langues", mission: "Apprenez un mot à chacun" },
    ],
  },
  {
    id: "parler",
    label: "Parler",
    sujets: [
      { id: "parler", label: "J'ai juste envie de parler", mission: "Posez une question inattendue" },
      { id: "nouveaux", label: "Je viens d'arriver dans le quartier", mission: "Faites-vous raconter le quartier" },
      { id: "silence", label: "J'aime les gens qui ne parlent pas trop", mission: "Restez une minute sans rien dire" },
      { id: "travail", label: "J'ai envie de parler d'autre chose que du travail", mission: "Interdit de dire son métier" },
      { id: "projets", label: "J'ai un projet et personne à qui en parler", mission: "Chacun raconte le sien en deux minutes" },
      { id: "conseils", label: "J'aime donner et recevoir des conseils", mission: "Demandez un conseil à voix haute" },
    ],
  },
];

export const SUJETS: readonly Sujet[] = FAMILLES.flatMap((f) => f.sujets);

export function sujet(id: string): Sujet | undefined {
  return SUJETS.find((s) => s.id === id);
}


/** Couleurs de reconnaissance. Le nom de la tribu est celui de sa couleur. */
export interface CouleurTribu {
  id: string;
  tribeName: string;
  color: string;
  signal: string;
  sound: string;
}

export const COULEURS: readonly CouleurTribu[] = [
  { id: "bleue", tribeName: "Bleue", color: "#4ba6ff", signal: "Cherchez le bleu", sound: "motif-01" },
  { id: "corail", tribeName: "Corail", color: "#ff6a5f", signal: "Cherchez le corail", sound: "motif-02" },
  { id: "violette", tribeName: "Violette", color: "#a967ff", signal: "Cherchez le violet", sound: "motif-03" },
  { id: "jaune", tribeName: "Jaune", color: "#ffc23e", signal: "Cherchez le jaune", sound: "motif-04" },
  { id: "turquoise", tribeName: "Turquoise", color: "#43c7c4", signal: "Cherchez le turquoise", sound: "motif-05" },
  { id: "ambre", tribeName: "Ambre", color: "#dcae63", signal: "Cherchez l'ambre", sound: "motif-06" },
  { id: "menthe", tribeName: "Menthe", color: "#6fe3a1", signal: "Cherchez le vert clair", sound: "motif-07" },
  { id: "rose", tribeName: "Rose", color: "#ff8fc7", signal: "Cherchez le rose", sound: "motif-08" },
  { id: "indigo", tribeName: "Indigo", color: "#7c7cff", signal: "Cherchez l'indigo", sound: "motif-09" },
  { id: "cuivre", tribeName: "Cuivre", color: "#e08a4a", signal: "Cherchez le cuivre", sound: "motif-10" },
];

export function couleur(id: string): CouleurTribu | undefined {
  return COULEURS.find((c) => c.id === id);
}

/**
 * Tire une couleur au hasard parmi celles qui ne sont pas déjà prises dans le
 * quartier. Renvoie null si toutes sont occupées : mieux vaut refuser que de
 * rendre deux tribus indiscernables sur place.
 */
export function couleurLibre(prises: readonly string[], alea: () => number = Math.random): CouleurTribu | null {
  const libres = COULEURS.filter((c) => !prises.includes(c.id));
  if (libres.length === 0) return null;
  return libres[Math.floor(alea() * libres.length)] ?? null;
}
